import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import {
  Play,
  Pause,
  SkipForward,
  SkipBack,
  Volume2,
  VolumeX,
  Shuffle,
  Repeat,
  Repeat1,
  Music,
  ChevronDown,
  ChevronUp,
  List,
} from "lucide-react";
import { useMusicPlayer } from "@/lib/store/music-player-store";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { defaultPlaylist } from "@/lib/music-playlist";

function formatTime(seconds: number): string {
  if (isNaN(seconds) || !isFinite(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

interface MusicPlayerProps {
  isInSheet?: boolean;
}

export function MusicPlayer({ isInSheet = false }: MusicPlayerProps = {}) {
  const {
    isPlaying,
    currentTrackIndex,
    volume,
    isShuffled,
    repeatMode,
    playlist,
    currentTime,
    duration,
    isExpanded,
    togglePlay,
    next,
    previous,
    setVolume,
    toggleShuffle,
    setRepeatMode,
    setTrack,
    setCurrentTime,
    setPlaylist,
    toggleExpanded,
  } = useMusicPlayer();

  const [showPlaylist, setShowPlaylist] = useState(false);
  const [playlistPosition, setPlaylistPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const playlistButtonRef = useRef<HTMLButtonElement>(null);
  const playlistDropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const expandedViewRef = useRef<HTMLDivElement>(null);
  const [expandedPosition, setExpandedPosition] = useState<{ top: number; left: number; width: number } | null>(null);

  // Calculate playlist dropdown position
  useEffect(() => {
    const updatePosition = () => {
      if (showPlaylist && playlistButtonRef.current) {
        const rect = playlistButtonRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const margin = 8; // px padding from viewport edges
        const dropdownWidth = Math.max(200, rect.width);
        const dropdownHeight = Math.min(256, playlist.length * 32 + 40); // Estimate height
        
        // Start aligned with the button's left edge
        let left = rect.left + window.scrollX;
        
        // Check if dropdown would overflow to the right
        const overflowRight = left + dropdownWidth - (window.scrollX + viewportWidth - margin);
        if (overflowRight > 0) {
          // Try to align to the right edge of the button
          left = rect.right + window.scrollX - dropdownWidth;
          // If that still goes off the left, align to viewport edge
          if (left < window.scrollX + margin) {
            left = window.scrollX + margin;
          }
        }
        
        // Ensure we never go off the left edge
        left = Math.max(window.scrollX + margin, left);
        
        // Calculate top position
        let top = rect.bottom + window.scrollY + 4;
        
        // Check if dropdown would overflow to the bottom
        const overflowBottom = top + dropdownHeight - (window.scrollY + viewportHeight - margin);
        if (overflowBottom > 0) {
          // Position above the button instead
          top = rect.top + window.scrollY - dropdownHeight - 4;
          // If that goes off the top, align to viewport top
          if (top < window.scrollY + margin) {
            top = window.scrollY + margin;
          }
        }
        
        setPlaylistPosition({
          top,
          left,
          width: dropdownWidth,
        });
      } else {
        setPlaylistPosition(null);
      }
    };

    if (showPlaylist) {
      updatePosition();
      // Update position on scroll and resize
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
      
      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [showPlaylist, playlist.length]);

  // Close playlist when clicking outside
  useEffect(() => {
    if (!showPlaylist) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        playlistButtonRef.current &&
        playlistDropdownRef.current &&
        !playlistButtonRef.current.contains(event.target as Node) &&
        !playlistDropdownRef.current.contains(event.target as Node)
      ) {
        setShowPlaylist(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showPlaylist]);

  // Close expanded view when clicking outside
  useEffect(() => {
    if (!isExpanded) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        expandedViewRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        !expandedViewRef.current.contains(event.target as Node) &&
        !playlistDropdownRef.current?.contains(event.target as Node)
      ) {
        toggleExpanded();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isExpanded, toggleExpanded]);

  // Initialize playlist on mount
  useEffect(() => {
    if (playlist.length === 0 && defaultPlaylist.length > 0) {
      setPlaylist(defaultPlaylist);
    }
  }, [playlist.length, setPlaylist]);

  // Calculate expanded view position when it opens and update on scroll/resize
  useEffect(() => {
    if (!isExpanded || !containerRef.current) {
      setExpandedPosition(null);
      return;
    }

    const updatePosition = () => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const margin = 8; // px padding from viewport edges

      // Fixed width: 600px for full controls, but never wider than viewport - margins
      const maxAllowedWidth = Math.max(0, viewportWidth - margin * 2);
      const width = Math.min(600, maxAllowedWidth);

      // Start aligned with the container's left edge
      let left = rect.left + window.scrollX;

      // If it would overflow to the right, shift it left
      const overflowRight = left + width - (window.scrollX + viewportWidth - margin);
      if (overflowRight > 0) {
        left -= overflowRight;
      }

      // Ensure we never go off the left edge
      left = Math.max(window.scrollX + margin, left);

      setExpandedPosition({
        top: rect.bottom + window.scrollY + 4,
        left,
        width,
      });
    };

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isExpanded]);

  const currentTrack = playlist.length > 0 && currentTrackIndex < playlist.length
    ? playlist[currentTrackIndex]
    : null;

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  const handleProgressChange = (value: number[]) => {
    const percent = value[0] ?? 0;
    const newTime = (percent / 100) * duration;
    setCurrentTime(newTime);
  };

  const handleVolumeChange = (value: number[]) => {
    const percent = value[0] ?? 0;
    setVolume(percent / 100);
  };

  const cycleRepeatMode = () => {
    if (repeatMode === "off") {
      setRepeatMode("all");
    } else if (repeatMode === "all") {
      setRepeatMode("one");
    } else {
      setRepeatMode("off");
    }
  };

  if (playlist.length === 0) {
    return null; // Don't show player if no tracks
  }

  // Mobile Sheet layout - always expanded, full width
  if (isInSheet) {
    return (
      <div className="w-full flex flex-col gap-4">
        {/* Track Info */}
        {currentTrack && (
          <div className="flex items-center gap-2">
            <Music className="h-5 w-5 text-muted-foreground" />
            <span className="text-base font-medium text-foreground">{currentTrack.title}</span>
          </div>
        )}

        {/* Playback Controls */}
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="ghost"
            size="lg"
            onClick={previous}
            disabled={playlist.length <= 1}
            className="flex-shrink-0 text-muted-foreground hover:text-foreground p-2 h-10 w-10"
            aria-label="Previous track"
          >
            <SkipBack className="h-5 w-5" />
          </Button>
          <Button
            variant="ghost"
            size="lg"
            onClick={togglePlay}
            disabled={!currentTrack}
            className="flex-shrink-0 text-muted-foreground hover:text-foreground p-2 h-12 w-12"
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? (
              <Pause className="h-6 w-6" />
            ) : (
              <Play className="h-6 w-6" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="lg"
            onClick={next}
            disabled={playlist.length <= 1}
            className="flex-shrink-0 text-muted-foreground hover:text-foreground p-2 h-10 w-10"
            aria-label="Next track"
          >
            <SkipForward className="h-5 w-5" />
          </Button>
        </div>

        {/* Progress Bar */}
        <div className="flex items-center gap-2 w-full">
          <span className="text-xs text-muted-foreground tabular-nums flex-shrink-0 w-12">
            {formatTime(currentTime)}
          </span>
          <div className="relative flex-1 min-w-0">
            <Slider
              value={[progressPercent]}
              onValueChange={handleProgressChange}
              min={0}
              max={100}
              step={0.1}
              disabled={!currentTrack || duration === 0}
              className="w-full"
            />
          </div>
          <span className="text-xs text-muted-foreground tabular-nums flex-shrink-0 w-12">
            {formatTime(duration)}
          </span>
        </div>

        {/* Volume and Options */}
        <div className="flex items-center justify-between gap-4">
          {/* Volume Control */}
          <div className="flex items-center gap-2 flex-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setVolume(volume > 0 ? 0 : 0.7)}
              className="flex-shrink-0 text-muted-foreground hover:text-foreground p-2 h-9 w-9"
              aria-label={volume > 0 ? "Mute" : "Unmute"}
            >
              {volume > 0 ? (
                <Volume2 className="h-4 w-4" />
              ) : (
                <VolumeX className="h-4 w-4" />
              )}
            </Button>
            <div className="relative flex-1 min-w-0">
              <Slider
                value={[volume * 100]}
                onValueChange={handleVolumeChange}
                min={0}
                max={100}
                step={1}
                className="w-full"
              />
            </div>
          </div>

          {/* Shuffle, Repeat, Playlist */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleShuffle}
              className={`flex-shrink-0 p-2 h-9 w-9 ${
                isShuffled
                  ? "text-primary hover:text-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              aria-label="Toggle shuffle"
            >
              <Shuffle className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={cycleRepeatMode}
              className={`flex-shrink-0 p-2 h-9 w-9 ${
                repeatMode !== "off"
                  ? "text-primary hover:text-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              aria-label={`Repeat mode: ${repeatMode}`}
            >
              {repeatMode === "one" ? (
                <Repeat1 className="h-4 w-4" />
              ) : (
                <Repeat className="h-4 w-4" />
              )}
            </Button>
            <Button
              ref={playlistButtonRef}
              variant="ghost"
              size="sm"
              onClick={() => setShowPlaylist(!showPlaylist)}
              className="flex-shrink-0 text-muted-foreground hover:text-foreground p-2 h-9 w-9"
              aria-label="Toggle playlist"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Playlist Dropdown - inline for mobile */}
        {showPlaylist && (
          <div
            ref={playlistDropdownRef}
            className="w-full bg-popover text-popover-foreground rounded-md border shadow-lg max-h-64 overflow-y-auto"
          >
            <div className="p-2">
              <div className="text-xs font-semibold text-muted-foreground mb-2 px-2">
                Playlist ({playlist.length})
              </div>
              <div className="space-y-0.5">
                {playlist.map((track, index) => (
                  <button
                    key={track.id}
                    onClick={() => {
                      setTrack(index);
                      setShowPlaylist(false);
                    }}
                    className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors ${
                      index === currentTrackIndex
                        ? "bg-primary/10 text-primary font-medium"
                        : "text-foreground hover:bg-accent/50"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {index === currentTrackIndex && isPlaying && (
                        <Music className="h-3 w-3 animate-pulse" />
                      )}
                      <span className="truncate">{track.title}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Desktop navbar layout - collapsed/expanded with portal overlay
  return (
    <>
      {/* Container - always rendered to maintain navbar height with fixed width */}
      <div ref={containerRef} className="flex items-center gap-2 w-[200px] flex-shrink-0">
        {/* Collapsed View */}
        <div className="flex items-center gap-2 w-full min-w-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={togglePlay}
            disabled={!currentTrack}
            className="flex-shrink-0 text-muted-foreground hover:text-foreground p-0 h-8 w-8"
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? (
              <Pause className="h-4 w-4" />
            ) : (
              <Play className="h-4 w-4" />
            )}
          </Button>
          {currentTrack && (
            <button
              onClick={toggleExpanded}
              className="text-xs text-muted-foreground hover:text-foreground truncate flex-1 min-w-0 text-left"
              title={currentTrack.title}
            >
              {currentTrack.title}
            </button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleExpanded}
            className="flex-shrink-0 text-muted-foreground hover:text-foreground p-0 h-8 w-8"
            aria-label={isExpanded ? "Collapse player" : "Expand player"}
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronUp className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Expanded View - positioned absolutely to not affect navbar height */}
      {isExpanded && expandedPosition && createPortal(
        <div 
          ref={expandedViewRef}
          className="fixed z-50 flex items-center gap-2 bg-background/95 backdrop-blur-sm border border-border rounded-lg px-2 py-1.5 shadow-sm"
          style={{
            top: `${expandedPosition.top}px`,
            left: `${expandedPosition.left}px`,
            width: `${expandedPosition.width}px`,
          }}
        >
          <div className="flex items-center gap-1.5 min-w-0 w-full">
            {/* Track Info */}
            {currentTrack && (
              <div className="flex items-center gap-1.5 min-w-0 flex-shrink w-[120px]">
                <Music className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="text-xs font-medium text-foreground truncate w-full min-w-0" title={currentTrack.title}>
                  {currentTrack.title}
                </span>
              </div>
            )}

            {/* Playback Controls */}
            <div className="flex items-center gap-0.5">
              <Button
                variant="ghost"
                size="sm"
                onClick={previous}
                disabled={playlist.length <= 1}
                className="flex-shrink-0 text-muted-foreground hover:text-foreground p-1 h-7 w-7"
                aria-label="Previous track"
              >
                <SkipBack className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={togglePlay}
                disabled={!currentTrack}
                className="flex-shrink-0 text-muted-foreground hover:text-foreground p-1 h-7 w-7"
                aria-label={isPlaying ? "Pause" : "Play"}
              >
                {isPlaying ? (
                  <Pause className="h-3.5 w-3.5" />
                ) : (
                  <Play className="h-3.5 w-3.5" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={next}
                disabled={playlist.length <= 1}
                className="flex-shrink-0 text-muted-foreground hover:text-foreground p-1 h-7 w-7"
                aria-label="Next track"
              >
                <SkipForward className="h-3.5 w-3.5" />
              </Button>
            </div>

            {/* Progress Bar */}
            <div className="flex items-center gap-1.5 min-w-0 flex-1 max-w-[300px] sm:max-w-[400px] md:max-w-[500px] lg:max-w-[600px]">
              <span className="text-[10px] text-muted-foreground tabular-nums flex-shrink-0">
                {formatTime(currentTime)}
              </span>
              <div className="relative flex-1 min-w-0 w-20">
                <Slider
                  value={[progressPercent]}
                  onValueChange={handleProgressChange}
                  min={0}
                  max={100}
                  step={0.1}
                  disabled={!currentTrack || duration === 0}
                  className="w-full [&>div>div]:h-3 [&>div>button]:h-8 [&>div>button]:w-8"
                />
              </div>
              <span className="text-[10px] text-muted-foreground tabular-nums flex-shrink-0">
                {formatTime(duration)}
              </span>
            </div>

            {/* Volume Control */}
            <div className="flex items-center gap-1.5">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setVolume(volume > 0 ? 0 : 0.7)}
                className="flex-shrink-0 text-muted-foreground hover:text-foreground p-1 h-7 w-7"
                aria-label={volume > 0 ? "Mute" : "Unmute"}
              >
                {volume > 0 ? (
                  <Volume2 className="h-3.5 w-3.5" />
                ) : (
                  <VolumeX className="h-3.5 w-3.5" />
                )}
              </Button>
              <div className="relative w-10 sm:w-10">
                <Slider
                  value={[volume * 100]}
                  onValueChange={handleVolumeChange}
                  min={0}
                  max={100}
                  step={1}
                  className="w-full [&>div>div]:h-1.5 [&>div>button]:h-4 [&>div>button]:w-4"
                />
              </div>
            </div>

            {/* Shuffle Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleShuffle}
              className={`flex-shrink-0 p-1 h-7 w-7 ${
                isShuffled
                  ? "text-primary hover:text-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              aria-label="Toggle shuffle"
            >
              <Shuffle className="h-3.5 w-3.5" />
            </Button>

            {/* Repeat Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={cycleRepeatMode}
              className={`flex-shrink-0 p-1 h-7 w-7 ${
                repeatMode !== "off"
                  ? "text-primary hover:text-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              aria-label={`Repeat mode: ${repeatMode}`}
            >
              {repeatMode === "one" ? (
                <Repeat1 className="h-3.5 w-3.5" />
              ) : (
                <Repeat className="h-3.5 w-3.5" />
              )}
            </Button>

            {/* Playlist Toggle */}
            <Button
              ref={playlistButtonRef}
              variant="ghost"
              size="sm"
              onClick={() => setShowPlaylist(!showPlaylist)}
              className="flex-shrink-0 text-muted-foreground hover:text-foreground p-1 h-7 w-7"
              aria-label="Toggle playlist"
            >
              <List className="h-3.5 w-3.5" />
            </Button>

            {/* Collapse Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleExpanded}
              className="flex-shrink-0 text-muted-foreground hover:text-foreground p-1 h-7 w-7"
              aria-label="Collapse player"
            >
              <ChevronDown className="h-3.5 w-3.5" />
            </Button>
          </div>

          {/* Playlist Dropdown - rendered via portal */}
          {showPlaylist && playlistPosition && createPortal(
            <div
              ref={playlistDropdownRef}
              className="fixed z-50 bg-popover text-popover-foreground rounded-md border shadow-lg max-h-64 overflow-y-auto"
              style={{
                top: `${playlistPosition.top}px`,
                left: `${playlistPosition.left}px`,
                width: `${playlistPosition.width}px`,
              }}
            >
              <div className="p-2">
                <div className="text-xs font-semibold text-muted-foreground mb-2 px-2">
                  Playlist ({playlist.length})
                </div>
                <div className="space-y-0.5">
                  {playlist.map((track, index) => (
                    <button
                      key={track.id}
                      onClick={() => {
                        setTrack(index);
                        setShowPlaylist(false);
                      }}
                      className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors ${
                        index === currentTrackIndex
                          ? "bg-primary/10 text-primary font-medium"
                          : "text-foreground hover:bg-accent/50"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {index === currentTrackIndex && isPlaying && (
                          <Music className="h-3 w-3 animate-pulse" />
                        )}
                        <span className="truncate">{track.title}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>,
            document.body
          )}
        </div>,
        document.body
      )}
    </>
  );
}

